import { getWalletConfig } from './helpers/index.js';
import {
	insertOrUpdatePricingPlacements,
	insertOrUpdateFacetPlacement
} from './pricing/index.js';

import {
	INSTANT_EPRICE,
	INSTANT_EPRICE_SHOW_FOR_MYWALLET,
	INTEGRATION_NAME,
	SHOW_PAYMENTS_NOTIFICATION,
	USER_SHOW_PAYMENTS_KEY
} from './global-constants.js';
import { isWIAPILoaded } from './utils/wait-for.js';
import { ddcLog } from './utils/logger.js';

(async APILoader => {
	// Create an instance of the API
	const API = await APILoader.create('mywallet');

	API.subscribe('page-load-v1', async (data) => {
		const config = await API.utils.getConfig();
		const dealerData = await API.utils.getDealerData();
		const { payload: pageData } = data;
		const { detailPage, searchPage, pageName } = pageData;
		const isNewComparePage = pageName === 'INVENTORY_VEHICLE_COMPARE_DEFAULT';

		const { InvData } = window.DDC;
		const isSRP = !!(searchPage && InvData);
		
		if (!(isSRP || detailPage || isNewComparePage)) {
			API.log('Not SRP or VDP or New Compare. Skipping.');
			return;
		}

		const updatePlacements = async (eventData, updateFacetPlacement=true) => {
			// If opening paymentDetails, we are already showing payments and can skip the rest.
			if (eventData?.modal === 'paymentDetails') {
				return;
			}

			// TODO: Call getWalletConfig() only once per page/interaction
			// -------------------------
			// On SRP, we call getWalletConfig for every vehicle.
			// The config doesn't change between listings
			// We can store this in a higher scope or memoize it
			let walletData = await getWalletConfig();
			if (eventData?.updated && eventData.updatedPaymentsInfo) {
				walletData = { ...walletData, ...eventData.updatedPaymentsInfo };
			}

			const isSectionsVDP = window?.DDC?.dataLayer?.page?.pageInfo?.pageId.includes('DETAIL_RESPONSIVE_SECTIONS_AUTO');
			if (!isSectionsVDP) {
				insertOrUpdatePricingPlacements(API, isSRP, walletData, config, dealerData, isNewComparePage, eventData);
			}
			
			// TODO Move this placement insert out of the pricing.js
			// That way if someone turns off pricing, you would still have
			// the entry point to Wallet in the search facets.
			if (updateFacetPlacement) {
				insertOrUpdateFacetPlacement(API, config);
			}
		}

		if(isNewComparePage){
			updatePlacements();
		}

		const enableShowPaymentsToggle = () => {
			if (window?.DDC?.pubsub) {
				window.DDC.pubsub.publish(SHOW_PAYMENTS_NOTIFICATION, {
					show: true,
					source: INTEGRATION_NAME
				});
			}
		}

		const enablePayments = async (event) => {
			
			await isWIAPILoaded();
			const userShowPayments = JSON.parse(localStorage.getItem(USER_SHOW_PAYMENTS_KEY));

			if (userShowPayments?.show !== true) {
				localStorage.setItem(USER_SHOW_PAYMENTS_KEY, JSON.stringify({show: true}));
				enableShowPaymentsToggle();
				updatePlacements(event);
			}

			API.privateUtils.hideDefaultPayments();
		}

		// When the list of vehicles changes.
		API.subscribe('vehicle-data-updated-v1', async () => {
			updatePlacements();
		});

		// When Wallet settings change.
		// TODO: update to use `wallet:updatePaymentsInfo:completed` once that event has been tested and is ready
		window.caiEventBus.on('wallet:updated', async (event) => {
			updatePlacements(event);
		});

		// TODO: for now we are using this only for payment option updates, later wallet:updated will be replaced with this
		window.caiEventBus.on('wallet:updatePaymentsInfo:completed', async (event) => {
			if (event?.updatedPayload?.paymentOption && event.updatedPayload.interactions?.hasUpdatedPaymentOption 
				&& event?.updatedPaymentsInfo) {
					updatePlacements(event);
			}
		});

		// When Wallet is opened.
		window.caiEventBus.on('wallet:open', async (event) => {
			await enablePayments(event);
		});

		// We listen for "Show Payments" toggle events from ws-inv-filters here.
		// ws-inv-filters listens for these events as well.
		// The widget notifies us of toggle state changes from user interactions
		// and we broadcast requests to change the toggle's state to the widget.
		if (window.DDC.pubsub) {
			window.DDC.pubsub.subscribe(SHOW_PAYMENTS_NOTIFICATION, async (notification) => {
				const { show, source, isOverride, forcePopup } = notification;
				// Ignore events triggered by this integration.
				if (source === INTEGRATION_NAME) {
					return;
				}


				if (show) {
					API.privateUtils.hideDefaultPayments();

					if (forcePopup || !isOverride) {
						window.caiEventBus.emit('wallet:open');
						updatePlacements();
					} else if (isOverride) { 
						// isOverride flag is sent from ws-inv-filters 
						// when show payments is turned on opening wallet directly
						insertOrUpdateFacetPlacement(API, config);
					}
				} else {
					API.privateUtils.showDefaultPayments();
					updatePlacements(notification, false);
				}
			});

			window.DDC.pubsub.subscribe(INSTANT_EPRICE_SHOW_FOR_MYWALLET, (ePriceData) => {
				ddcLog('DDC: INSTANT_EPRICE_SHOW_FOR_MYWALLET ', ePriceData);
				const evenData = {
					...ePriceData,
					type: INSTANT_EPRICE
				};
				updatePlacements(evenData);
			});
		}

		const { paymentsToggleDefaultOn } = config;

		if (paymentsToggleDefaultOn) {
			enableShowPaymentsToggle();
		}

	});  

})(window.DDC.APILoader);
